/* eslint-disable max-lines */

import {
  EC2_ELASTIC_IP,
  EC2_IMAGE,
  EC2_INSTANCE,
  EC2_INTERNET_GATEWAY,
  EC2_KEY_PAIR,
  EC2_LAUNCH_TEMPLATE,
  EC2_LOAD_BALANCER,
  EC2_NETWORK_ACL,
  EC2_NETWORK_INTERFACE,
  EC2_PLACEMENT_GROUP,
  EC2_ROUTE_TABLE,
  EC2_SECURITY_GROUP,
  EC2_SNAPSHOT,
  EC2_SUBNET,
  EC2_VOLUME,
  EC2_VPC,
  IAM_GROUP,
  IAM_POLICY,
  IAM_ROLE,
  IAM_USER,
  LAMBDA_ALIAS,
  LAMBDA_EVENT_SOURCE_MAPPING,
  LAMBDA_FUNCTION,
  LAMBDA_LAYER,
  LAMBDA_VERSION,
  RDS_DB_CLUSTER,
  RDS_DB_CLUSTER_PARAMETER_GROUP,
  RDS_DB_INSTANCE,
  RDS_DB_PARAMETER_GROUP,
  RDS_DB_PROXY,
  RDS_DB_SECURITY_GROUP,
  RDS_DB_SUBNET_GROUP,
  RDS_OPTION_GROUP,
  ROUTE53_CIDR,
  ROUTE53_HEALTH_CHECK,
  ROUTE53_HOSTED_ZONE,
  ROUTE53_QUERY_LOGGING_CONFIG,
  ROUTE53_REUSABLE_DELEGATION_SET,
  ROUTE53_TRAFFIC_POLICY,
  ROUTE53_TRAFFIC_POLICY_INSTANCE,
  S3_ACCESS_POINT,
  S3_BUCKET,
} from '@/lib/types/resourceDefs';
import {
  IMPACTED_BY_UNMANAGED,
  IMPACTS_MANAGED,
  MANAGED,
  NOT_MANAGED,
} from '@/routes/_root/coverage/-components/CoverageTable/utils';

// This file is used as the source of truth for all other translations files format.
export const enUSMessages = {
  global: {
    anyshift: 'Anyshift',
    error: {
      title: 'Oops! An Error occurred...',
      goBack: 'Go back to home',
      stuck: 'Stuck? Contact us at: ',
      toggleTrace: 'Toggle trace',
      message: 'Message:',
      stack: 'Stack:',
      resourceNotFound: 'Resource not found',
    },
    menu: {
      title: 'Menu',
      signOut: 'Sign out',
      signOutError: 'Failed to sign out',
      search: 'Search',
      coverage: 'Coverage',
      configuration: 'Configuration',
      myProfile: 'My Profile',
      pullRequest: 'Pull Requests',
      settings: 'Settings',
      themeSettings: {
        title: 'Theme Settings',
        light: 'Light Mode',
        dark: 'Dark Mode',
        system: 'System Mode',
      },
      integrations: 'Integrations',
      catalog: 'Use Cases',
      welcome: 'Welcome {username}',
    },
    breadcrumb: {
      home: 'Home',
      resource: 'Resource',
      newProject: 'New project',
      profile: 'My profile',
      coverage: 'Cloud-to-code coverage',
      configuration: 'Configuration',
      pullRequests: 'My pull requests',
      newAWSCredentials: 'New AWS credentials',
      editAWSCredentials: 'Edit AWS credentials',
      newTerraformState: 'New Terraform integration',
      editTerraformState: 'Edit Terraform integration',
      newAWSRole: 'New AWS role',
      editAWSRole: 'Edit AWS role',
      integrations: 'Integrations',
      catalog: 'Use Cases',
      hcpCloud: 'HCP Cloud',
      newHCPCloudToken: 'New HCP Cloud token',
      editHCPCloudToken: 'Edit HCP Cloud token',
      newDatadogApiCredentials: 'New Datadog API credentials',
      editDatadogApiCredentials: 'Edit Datadog API credentials',
    },
    noValue: '-',
    noResults: 'No results',
    notAvailable: 'N/A',
    previous: 'Previous',
    next: 'Next',
    copiedToClipboard: 'Copied to clipboard',
    paginationOf: '{start} - {end} of {total} results',
    components: {
      dateTimePicker: {
        placeholder: 'Pick date and time',
      },
    },
  },
  githubCallback: {
    loader: {
      title: 'Installing Anyshift Github App',
      subtitle: 'Do not close the tab',
    },
    installationError:
      'Failed to create Github configuration, try to uninstall and install the app again',
    installationSuccess: 'Github configuration created',
    authenticationError: 'Failed to authenticate with Github, try again',
  },
  auth: {
    signUp: {
      title: 'Create your account',
      alreadyHaveAccount: 'Already have an account? ',
      signIn: 'Sign in',
      form: {
        email: {
          label: 'Email',
          placeholder: 'name@company.com',
          error: 'Should be a valid email',
        },
        password: {
          label: 'Password',
          placeholder: '••••••••',
          error: 'Should be at least {min} characters',
        },
        submit: {
          label: 'Sign up',
          pending: 'Signing up...',
          error: 'Failed to sign up, try again',
        },
      },
    },
    signIn: {
      title: 'Sign in to your account',
      noAccount: "Don't have an account yet? ",
      signUp: 'Sign up',
      forgotPassword: 'Forgot password?',
      form: {
        email: {
          label: 'Email',
          placeholder: 'name@company.com',
          error: 'Should be a valid email',
        },
        password: {
          label: 'Password',
          placeholder: '••••••••',
          error: 'Should be at least {min} characters',
        },
        submit: {
          label: 'Sign in',
          pending: 'Signing in...',
          error: 'Failed to sign in, check your credentials and try again',
        },
        rememberMe: 'Remember me',
      },
    },
    resetPasswordOnInvitation: {
      title: 'Welcome to Anyshift',
      subtitle: "First, let's set up your account",
      error: 'Failed to authenticate, check that the link is still valid',
    },
    resetPasswordOnForgotPassword: {
      title: 'Reset your password',
    },
    resetPassword: {
      form: {
        email: 'Email',
        password: {
          label: 'New password',
          placeholder: '••••••••',
          error: 'Should be at least {min} characters',
        },
        submit: {
          label: 'Update',
          pending: 'Updating...',
          error: 'Failed to reset password, try again',
          success: 'Password updated',
        },
      },
    },
    notVerified: {
      title: 'Please, check your email for the verification link',
      subtitle:
        'You need to verify your email address to complete the signup process. Please check your email and click on the verification link.',
    },
    forgotPassword: {
      title: 'Forgot your password?',
      form: {
        email: {
          label: 'You will receive an email with a password recovery link',
          placeholder: 'name@company.com',
          error: 'Should be a valid email',
        },
        submit: {
          label: 'Send recovery link',
          pending: 'Sending recovery link...',
          error: 'Failed to send recovery link, check the email and try again',
          success: 'Recovery link to your email',
        },
      },
    },
  },
  resource: {
    host: {
      hostedResources: {
        title: 'Hosted resources',
        resourceType: 'Resource type',
        service: 'Service',
        resourceId: 'Resource identifier',
      },
    },
    instance: {
      details: {
        title: 'Properties',
        region: {
          label: '🌎 AWS Region',
          description:
            'Click the link to explore resources we found in this region',
        },
        account: {
          label: '👤 AWS Account',
          description:
            'Click the link to explore resources we found in this account',
        },
        service: {
          label: '⚙️ AWS Service',
          description:
            'Click the link to explore resources we found in this service',
        },
        resourceType: {
          label: '📦 Resource type',
        },
      },
      definition: {
        title: 'Definition',
        awsURL: {
          label: '☁️ Link to AWS',
        },
        resourceCode: { label: '📂 Resource definition' },
        codeCreated: { label: '✨ Code creation date' },
        codeUpdated: { label: '🔄 Code last update' },
        moduleCode: { label: '📂 Module definition' },
        noCode: {
          label: '📂 Code definition',
          value: {
            notManaged: '⚠️ Unmanaged',
            na: '-',
          },
        },
      },
      linkToCoverage: {
        title: 'Cloud-to-code coverage',
        text: 'This resource was directly scanned in the cloud thanks to our new IAM engine.',
        link: 'Find detailled data about its IAC coverage, impacts and dependencies.',
      },
    },
    history: {
      title: 'Time machine',
      timeline: {
        current: 'Current state',
        createdOnCloud: 'Resource created on the Cloud',
        resourceDeletedOnCloud: 'Resource deleted on the Cloud',
        deleted: {
          code: 'Deleted from TF',
          cloud: 'Deleted from cloud',
        },
        detectedByAnyshift: 'Change detected by Anyshift',
        noChanges: 'No changes',
        updatedProperties:
          '{count} {count, plural, one {property} other {properties}} updated',
        newDownstreamImpacts:
          '{count} new downstream {count, plural, one {impact} other {impacts}}',
        deletedDownstreamImpacts:
          '{count} downstream {count, plural, one {impact} other {impacts}} deleted',
        newUpstreamImpacts:
          '{count} new upstream {count, plural, one {impact} other {impacts}}',
        deletedUpstreamImpacts:
          '{count} upstream {count, plural, one {impact} other {impacts}} deleted',
        createdOnTf: 'Source code added',
        updatedOnTf: 'Source code updated',
        deletedOnTf: 'Source code deleted',
        commitPushedOnRepository: '{sha} pushed on {repository}',
        timestamp: {
          header: '🕒 Timestamp',
        },
        updatedBy: {
          header: '👤 Updated by',
        },
        changes: {
          header: '🔎 Changes',
        },
        trigger: {
          header: '💥 Trigger',
        },
        switchToManaged: 'Resource is now managed',
        switchToUnmanaged: 'Resource is now unmanaged',
      },
    },
    updatesPeriod: {
      history: {
        noResults: 'No changes during this period',
      },
      impacts: {
        noResults: 'No impacted resources were updated during this period',
      },
      form: {
        title: 'Visualize updates in time interval:',
        clear: 'Clear',
        submit: 'Go!',
      },
    },
  },
  newProject: {
    title: 'Create a new project',
    form: {
      name: {
        label: 'Project name',
        error: {
          min: 'Should be at least {min} characters',
          max: 'Should be at most {max} characters',
        },
      },
      submit: {
        label: 'Create Project',
        pending: 'Creating...',
        error: 'Failed to create project, try again',
        success: 'Project created',
      },
    },
  },
  profile: {
    title: 'User account settings',
    email: 'Email address',
    updatePassword: {
      title: 'Change password',
      subtitle: 'Enter your new password below',
      form: {
        password: {
          label: 'New password',
          placeholder: '••••••••',
          error: 'Should be at least {min} characters',
        },
        submit: {
          label: 'Update password',
          pending: 'Updating password...',
          error: 'Failed to update password, try again',
          success: 'Password updated',
        },
      },
    },
  },
  coverage: {
    title: 'Cloud-to-code IaC coverage',
    overview: {
      charts: {
        managed:
          '{count, plural, one {{count} managed resource} other {{count} managed resources}}',
        unmanaged:
          '{count, plural, one {{count} unmanaged resource} other {{count} unmanaged resources}}',
      },
      table: {
        resourceType: {
          header: '📦 Resource type',
        },
        resourceId: { header: '🧩 Resource' },
        impactZone: {
          header: '💥 Impact zone',
          options: {
            [IMPACTS_MANAGED]:
              '{count, plural, one {🥊 Impacts {count} managed resource} other {🥊 Impacts {count} managed IAM resources}}',
            [IMPACTED_BY_UNMANAGED]:
              '{count, plural, one {🎯 Impacted by {count} unmanaged resource} other {🎯 Impacted by {count} unmanaged IAM resources}}',
          },
          headerOptions: {
            [IMPACTS_MANAGED]: '🥊 Impacts managed IAM resources',
            [IMPACTED_BY_UNMANAGED]: '🎯 Impacted by unmanaged IAM resources',
          },
        },
        updatedAt: {
          header: '🔄 Last update',
        },
        awsUrl: {
          header: '☁️ Link to AWS',
          cell: 'Open',
        },
        codeUrl: {
          header: '📂 Link to code',
          resource: 'Resource',
          module: 'Module',
          unmanaged: '⚠️ Unmanaged',
          headerOptions: {
            [MANAGED]: '✅ Managed',
            [NOT_MANAGED]: '⚠️ Unmanaged',
          },
        },
        impactType: {
          header: '💥 Impact type',
          options: {
            upstream: '🎯 Upstream',
            downstream: '🥊 Downstream',
          },
        },
        updates: {
          header: '🔄 Updates',
        },
      },
    },
    resourceTypes: {
      iam: {
        [IAM_ROLE.resourceType]: 'IAM Role',
        [IAM_POLICY.resourceType]: 'IAM policy',
        [IAM_USER.resourceType]: 'IAM User',
        [IAM_GROUP.resourceType]: 'IAM Group',
      },
      ec2: {
        [EC2_ELASTIC_IP.resourceType]: 'EC2 Elastic IP',
        [EC2_IMAGE.resourceType]: 'EC2 Image',
        [EC2_INSTANCE.resourceType]: 'EC2 Instance',
        [EC2_INTERNET_GATEWAY.resourceType]: 'EC2 Internet Gateway',
        [EC2_KEY_PAIR.resourceType]: 'EC2 Key Pair',
        [EC2_LAUNCH_TEMPLATE.resourceType]: 'EC2 Launch Template',
        [EC2_LOAD_BALANCER.resourceType]: 'EC2 Load Balancer',
        [EC2_NETWORK_ACL.resourceType]: 'EC2 Network ACL',
        [EC2_NETWORK_INTERFACE.resourceType]: 'EC2 Network Interface',
        [EC2_PLACEMENT_GROUP.resourceType]: 'EC2 Placement Group',
        [EC2_ROUTE_TABLE.resourceType]: 'EC2 Route Table',
        [EC2_SECURITY_GROUP.resourceType]: 'EC2 Security Group',
        [EC2_SNAPSHOT.resourceType]: 'EC2 Snapshot',
        [EC2_VPC.resourceType]: 'EC2 VPC',
        [EC2_SUBNET.resourceType]: 'EC2 Subnet',
        [EC2_VOLUME.resourceType]: 'EC2 Volume',
      },
      lambda: {
        [LAMBDA_ALIAS.resourceType]: 'Lambda Alias',
        [LAMBDA_EVENT_SOURCE_MAPPING.resourceType]:
          'Lambda Event Source Mapping',
        [LAMBDA_FUNCTION.resourceType]: 'Lambda Function',
        [LAMBDA_LAYER.resourceType]: 'Lambda Layer',
        [LAMBDA_VERSION.resourceType]: 'Lambda Version',
      },
      rds: {
        [RDS_DB_CLUSTER.resourceType]: 'RDS Cluster',
        [RDS_DB_CLUSTER_PARAMETER_GROUP.resourceType]:
          'RDS Cluster Parameter Group',
        [RDS_DB_INSTANCE.resourceType]: 'RDS Instance',
        [RDS_DB_PARAMETER_GROUP.resourceType]: 'RDS DB Parameter Group',
        [RDS_DB_PROXY.resourceType]: 'RDS Proxy',
        [RDS_DB_SECURITY_GROUP.resourceType]: 'RDS Security Group',
        [RDS_DB_SUBNET_GROUP.resourceType]: 'RDS Subnet Group',
        [RDS_OPTION_GROUP.resourceType]: 'RDS Option Group',
      },
      route53: {
        [ROUTE53_CIDR.resourceType]: 'Route53 CIDR',
        [ROUTE53_HEALTH_CHECK.resourceType]: 'Route53 Health Check',
        [ROUTE53_HOSTED_ZONE.resourceType]: 'Route53 Hosted Zone',
        [ROUTE53_QUERY_LOGGING_CONFIG.resourceType]:
          'Route53 Query Logging Config',
        [ROUTE53_REUSABLE_DELEGATION_SET.resourceType]:
          'Route53 Reusable Delegation Set',
        [ROUTE53_TRAFFIC_POLICY.resourceType]: 'Route53 Traffic Policy',
        [ROUTE53_TRAFFIC_POLICY_INSTANCE.resourceType]:
          'Route53 Traffic Policy Instance',
      },
      s3: {
        [S3_ACCESS_POINT.resourceType]: 'S3 Access Point',
        [S3_BUCKET.resourceType]: 'S3 Bucket',
      },
    },
    emojiResourceTypes: {
      iam: {
        [IAM_ROLE.resourceType]: '🎭 IAM Role',
        [IAM_POLICY.resourceType]: '🎟️ IAM Policy',
        [IAM_USER.resourceType]: '👤 IAM User',
        [IAM_GROUP.resourceType]: '👥 IAM Group',
      },
      ec2: {
        [EC2_ELASTIC_IP.resourceType]: '📡 EC2 Elastic IP',
        [EC2_IMAGE.resourceType]: '🖼️ EC2 Image',
        [EC2_INSTANCE.resourceType]: '🖥️ EC2 Instance',
        [EC2_INTERNET_GATEWAY.resourceType]: '🚪 EC2 Internet Gateway',
        [EC2_KEY_PAIR.resourceType]: '🔑 EC2 Key Pair',
        [EC2_LAUNCH_TEMPLATE.resourceType]: '📜 EC2 Launch Template',
        [EC2_LOAD_BALANCER.resourceType]: '⚖️ EC2 Load Balancer',
        [EC2_NETWORK_ACL.resourceType]: '🚦 EC2 Network ACL',
        [EC2_NETWORK_INTERFACE.resourceType]: '🔌 EC2 Network Interface',
        [EC2_PLACEMENT_GROUP.resourceType]: '📍 EC2 Placement Group',
        [EC2_ROUTE_TABLE.resourceType]: '🛣️ EC2 Route Table',
        [EC2_SECURITY_GROUP.resourceType]: '🛡️ EC2 Security Group',
        [EC2_SNAPSHOT.resourceType]: '📸 EC2 Snapshot',
        [EC2_VPC.resourceType]: '🏢 EC2 VPC',
        [EC2_SUBNET.resourceType]: '🌐 EC2 Subnet',
        [EC2_VOLUME.resourceType]: '💾 EC2 Volume',
      },
      lambda: {
        [LAMBDA_ALIAS.resourceType]: '🔗 Lambda Alias',
        [LAMBDA_EVENT_SOURCE_MAPPING.resourceType]:
          '🔄 Lambda Event Source Mapping',
        [LAMBDA_FUNCTION.resourceType]: '⚡ Lambda Function',
        [LAMBDA_LAYER.resourceType]: '📦 Lambda Layer',
        [LAMBDA_VERSION.resourceType]: '🔢 Lambda Version',
      },
      rds: {
        [RDS_DB_CLUSTER.resourceType]: '🗄️ RDS Cluster',
        [RDS_DB_CLUSTER_PARAMETER_GROUP.resourceType]: '⚙️ RDS Parameter Group',
        [RDS_DB_INSTANCE.resourceType]: '🖥️ RDS Instance',
        [RDS_DB_PARAMETER_GROUP.resourceType]: '⚙️ RDS Parameter Group',
        [RDS_DB_PROXY.resourceType]: '🔀 RDS Proxy',
        [RDS_DB_SECURITY_GROUP.resourceType]: '🛡️ RDS Security Group',
        [RDS_DB_SUBNET_GROUP.resourceType]: '🌐 RDS Subnet Group',
        [RDS_OPTION_GROUP.resourceType]: '🛠️ RDS Option Group',
      },
      route53: {
        [ROUTE53_CIDR.resourceType]: '📏 Route53 CIDR',
        [ROUTE53_HEALTH_CHECK.resourceType]: '💓 Route53 Health Check',
        [ROUTE53_HOSTED_ZONE.resourceType]: '🌍 Route53 Hosted Zone',
        [ROUTE53_QUERY_LOGGING_CONFIG.resourceType]:
          '📜 Route53 Query Logging Config',
        [ROUTE53_REUSABLE_DELEGATION_SET.resourceType]:
          '🔁 Route53 Reusable Delegation Set',
        [ROUTE53_TRAFFIC_POLICY.resourceType]: '🚦 Route53 Traffic Policy',
        [ROUTE53_TRAFFIC_POLICY_INSTANCE.resourceType]:
          '📍 Route53 Traffic Policy Instance',
      },
      s3: {
        [S3_ACCESS_POINT.resourceType]: '🔗 S3 Access Point',
        [S3_BUCKET.resourceType]: '️🪣 S3 Bucket',
      },
    },
    details: {
      linkToAWS: 'Link to AWS',
      impactAnalysis: {
        noImpact:
          'This {managed, select, true {managed} other {unmanaged}} resource {verb} IAM resources',
        impactsManaged:
          'This {managed, select, true {managed} other {unmanaged}} resource {verb} {managedCount, plural, one {{managedCount} managed IAM resource} other {{managedCount} managed IAM resources}}',
        impactsUnmanaged:
          'This {managed, select, true {managed} other {unmanaged}} resource {verb} {unmanagedCount, plural, one {{unmanagedCount} unmanaged IAM resource} other {{unmanagedCount} unmanaged IAM resources}}',
        impactsBoth:
          'This {managed, select, true {managed} other {unmanaged}} resource {verb} {managedCount, plural, one {{managedCount} managed IAM resource} other {{managedCount} managed IAM resources}} and {unmanagedCount, plural, one {{unmanagedCount} unmanaged IAM resource} other {{unmanagedCount} unmanaged IAM resources}}',
        downstream: {
          label: '🥊 Downstream impact',
        },
        upstream: {
          label: '🎯 Upstream impact',
        },
        title: 'Impact analysis',
        managedBy: '• This resource is managed in terraform by ',
        unmanaged: "• This resource isn't managed",
      },
      statistics: {
        title: 'Statistics',
        createdAt: '✨ Creation date',
        updatedAt: '🔄 Last update',
        coverage: ' IAC coverage',
      },
      impactNotAvailable: {
        title: 'Impact analysis',
        text1:
          '• Cloud Impact analysis does not support resources from "{service}" service yet',
        text2: '• New services are on the roadmap, contact us at',
        text3: 'if you want yours done first!',
      },
      impacts: 'Impacted resources',
      intervalFromTo: '{prefix} between {from} and {to}',
      intervalFrom: '{prefix} since {from}',
      intervalTo: '{prefix} until {to}',
    },
  },
  search: {
    title: 'Cloud-to-Code Search',
    form: {
      searchTerm: {
        placeholder: 'Search for any cloud resource',
      },
      submit: {
        label: 'Search',
        pending: 'Searching...',
      },
    },
    table: {
      awsUrl: {
        header: '☁️ Link to AWS',
        cell: 'Open',
      },
      codeUrl: {
        header: '📂 Link to code',
        cell: 'Open',
        resource: 'Resource',
        module: 'Module',
        unmanaged: '⚠️ Unmanaged',
      },
      codeLastUpdatedAt: {
        header: '🔄 Last update',
      },
      codeLastUpdatedBy: {
        header: '👤 Updated by',
      },
      numberOfUpdates: {
        header: '🔄 Updates',
      },
      resourceType: {
        header: '📦 Resource type',
      },
      resourceId: {
        header: '🧩 Resource',
      },
      account: {
        header: '👤 Account',
      },
      region: {
        header: '🌎 Region',
      },
      service: {
        header: '🛠️ Service',
      },
      matchedProperties: {
        header: '🔎 Matched properties',
      },
      deleted: {
        header: '🗑️ Status',
        options: {
          deleted: '🔴 Deleted',
          active: '🟢 Active',
        },
      },
    },
    changes: {
      title: 'Search for updates between:',
      clear: 'Clear',
      showDeleted: 'Show resources deleted during the selected interval',
    },
  },
  integrations: {
    title: 'Add data to Anyshift',
    subtitle: 'Integrations',
    categories: {
      scm: {
        title: 'SCM',
        github: 'Github',
        gitlab: 'Gitlab',
      },
      publicCloud: {
        title: 'Public Cloud',
        aws: 'AWS',
        gcp: 'GCP',
      },
      monitoring: {
        title: 'Monitoring',
        datadog: 'Datadog',
      },
      containers: {
        title: 'Containers',
        kubernetes: 'Kubernetes',
      },
      terraformBackend: {
        title: 'Terraform Backend',
        s3: 'S3',
        hcp: 'HCP',
      },
    },
    scm: {
      tryIt:
        'Code data is now connected with Anyshift<button>Try it now!</button>',
    },
    aws: {
      title: 'Integrate with AWS',
      cta: 'Anyshift needs access to AWS to periodically scan your cloud resources. You can give Anyshift access to your AWS by creating an IAM user or an IAM role.{br}Ideally, use the <a>ReadOnlyAccess</a> policy.',
      tryIt:
        'AWS Cloud data is now connected with Anyshift<button>Try it now!</button>',
      roles: {
        title: 'IAM Roles',
        new: 'New role',
        table: {
          name: {
            header: 'Role name',
          },
          edit: 'Edit role',
          delete: 'Delete role',
          status: {
            header: 'Status',
          },
        },
      },
      users: {
        title: 'IAM Users',
        new: 'New user',
        table: {
          name: {
            header: 'User name',
          },
          edit: 'Edit user',
          delete: 'Delete user',
          status: {
            header: 'Status',
          },
        },
      },
      status: {
        active: '🟢 Active',
        inactive: '🔄 Syncing',
        error: '🔴 Failed to fetch AWS resources',
      },
      roleError:
        'Anyshift failed to assume this role to fetch AWS resources, check if the role exists, its trust relationship policy and the permissions policy attached to it.',
      userError:
        'Anyshift failed to use these credentials to fetch AWS resources, check if the user exists and the permissions policy attached to it.',
      warningRole: '• One of the roles failed to fetch AWS resources',
      warningUser: '• One of the users failed to fetch AWS resources',
    },
    terraformBackend: {
      buckets: {
        title: 'Integrate with your Terraform state',
        cta: 'Link your cloud resources with their code definition by providing us with your Terraform state files.',
        list: {
          title: 'S3 Buckets',
          table: {
            name: {
              header: 'Name',
            },
            region: {
              header: 'Region',
            },
            status: {
              header: 'Status',
              options: {
                active: '🟢 Active',
                synced: '🔄 Syncing',
                'unsynced-with-cloud': '🟠 Not found on the cloud',
                'no-creds': '🔴 No credentials able to access bucket content',
                error: '🔴 Credentials failed to access bucket content',
              },
              'unsynced-with-cloud':
                'This bucket is configured as the backend of one of your Terraform directories, but we could not find it on AWS.{br}Update your credentials or add new ones to complete the integration.',
              error:
                'This bucket is configured as the backend of one of your Terraform directories, we found it on AWS, but the following credentials failed to access its content:{creds}{br}Update your credentials or add new ones to complete the integration.',
              'no-creds':
                'This bucket is configured as the backend of one of your Terraform directories, we found it on AWS, but we could not find any credentials able to access its content.{br}Update your credentials or add new ones to complete the integration.',
            },
          },
        },
        warningUnsynced:
          '• Some buckets referenced in your code were not found on AWS',
        warningAccess:
          '• Your AWS credentials failed to access some buckets content',
      },
      hcpCloud: {
        title: 'Integrate with HCP Cloud',
        cta: 'Link your cloud resources with their code definition by providing us with the state files found in your HCP Cloud workspaces.',
        tryIt:
          'Terraform states data is now connected with Anyshift<button>Try it now!</button>',
        list: {
          title: 'HCP Cloud tokens',
          new: 'New token',
          table: {
            name: {
              header: 'Name',
            },
            url: {
              header: 'HCP Cloud URL',
            },
            workspace: {
              header: 'Workspace',
            },
            edit: 'Edit token',
            delete: 'Delete token',
            status: {
              header: 'Status',
              options: {
                active: '🟢 Active',
                inactive: '🔄 Syncing',
                error: '🔴 Failed to acces HCP Cloud workspace',
              },
              error: {
                tooltip:
                  'This token failed to access the HCP Cloud workspace, check if the token is valid and has access to the workspace.',
              },
            },
          },
          tokenDeleted: 'HCP Cloud token deleted',
        },
        warningError: 'Some tokens failed to access HCP Cloud workspaces',
        form: {
          title: 'HCP Cloud token',
          subtitle: 'Create or update your HCP Cloud tokens',
          name: {
            label: 'Display name',
            placeholder: 'Prod token',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          url: {
            label: 'HCP Cloud URL',
            placeholder: 'https://app.terraform.io',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          workspace: {
            label: 'Workspace',
            placeholder: 'organization/workspace',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          token: {
            label: 'Token',
            placeholder: 'Secret token',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          create: {
            label: 'Create HCP Cloud token',
            pending: 'Creating...',
            error: 'Failed to create HCP Cloud token, try again',
            success: 'HCP Cloud token created',
          },
          update: {
            label: 'Update HCP Cloud token',
            pending: 'Updating...',
            error: 'Failed to update HCP Cloud token, try again',
            success: 'HCP Cloud token updated',
          },
          delete: {
            label: 'Delete HCP Cloud token',
            pending: 'Deleting...',
            error: 'Failed to delete HCP Cloud token, try again',
            success: 'HCP Cloud token deleted',
          },
        },
      },
    },
    monitoring: {
      datadog: {
        title: 'Integrate with Datadog',
        cta: 'Plug live data from Datadog into Anyshift to get real-time alerts and insights on your cloud resources.',
        tryIt:
          'Datadog data is now connected with Anyshift<button>Try it now!</button>',
        list: {
          title: 'Datadog API credentials',
          new: 'New API credentials',
          table: {
            name: {
              header: 'Name',
            },
            site: {
              header: 'Datadog site url',
            },
            edit: 'Edit credentials',
            delete: 'Delete credentials',
            status: {
              header: 'Status',
              options: {
                active: '🟢 Active',
                inactive: '🔄 Syncing',
                error: '🔴 Failed to acces Datadog API',
              },
              error: {
                tooltip:
                  'This token failed to access the Datadog API, check if the token is valid and has access to the Datadog API.',
              },
            },
          },
          apiCredentialsDeleted: 'API credentials deleted',
          newDisabled:
            'For now, only one Datadog API key per project is supported. Please delete the existing one to create a new one.',
        },
        warningError: 'Some API credentials failed to access Datadog API',
        form: {
          title: 'Datadog API credentials',
          subtitle: 'Create or update your Datadog API credentials',
          name: {
            label: 'Display name',
            placeholder: 'Prod credentials',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          site: {
            label: 'Datadog site URL',
            placeholder: 'datadoghq.com',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          apiKey: {
            label: 'API key',
            placeholder: 'API key',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          appKey: {
            label: 'Application key',
            placeholder: 'Application key',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          create: {
            label: 'Create Datadog API credentials',
            pending: 'Creating...',
            error: 'Failed to create Datadog API credentials, try again',
            success: 'Datadog API credentials created',
          },
          update: {
            label: 'Update Datadog API credentials',
            pending: 'Updating...',
            error: 'Failed to update Datadog API credentials, try again',
            success: 'Datadog API credentials updated',
          },
          delete: {
            label: 'Delete Datadog API credentials',
            pending: 'Deleting...',
            error: 'Failed to delete Datadog API credentials, try again',
            success: 'Datadog API credentials deleted',
          },
        },
      },
    },
    tryIt: {
      title: 'Try it!',
      connect: 'Connect',
      import: 'Connect {value}',
      examples: {
        hardcodedValues: 'Find hardcoded values in my IAC',
        vpcIdList:
          'Give me the VPC IDs and CIDR of each default VPCs and subnets',
        administratorAccessWithCode:
          'List all IAM users with AdministratorAccess Policy and their link to the code',
        podsStuckOnPending: 'Why are my pods stuck on pending?',
      },
    },
  },
  configuration: {
    title: 'Project configuration - {projectName}',
    githubSettings: {
      title: 'Github integration',
      button: 'Install github app',
      repositories:
        '{count, plural, =0 {No repositories ⚠️} one {{count} repository} other {{count} repositories}}',
      error: 'Failed to fetch Github configurations',
    },
    awsCredentials: {
      title: 'AWS integration',
      button: 'Add AWS credentials',
      form: {
        title: 'AWS credentials',
        accountName: {
          label: 'Account name',
          placeholder: 'Account name',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        accessKey: {
          label: 'Access key',
          placeholder: 'Access key',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        secretKey: {
          label: 'Secret key',
          placeholder: 'Secret key',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        create: {
          label: 'Create AWS credentials',
          pending: 'Creating...',
          error: 'Failed to create AWS credentials, try again',
          success: 'AWS credentials created',
        },
        update: {
          label: 'Update AWS credentials',
          pending: 'Updating...',
          error: 'Failed to update AWS credentials, try again',
          success: 'AWS credentials updated',
        },
        delete: {
          label: 'Delete AWS credentials',
          pending: 'Deleting...',
          error: 'Failed to delete AWS credentials, try again',
          success: 'AWS credentials deleted',
        },
      },
      error: 'Failed to fetch AWS credentials',
    },
    awsRole: {
      title: 'AWS integration',
      button: 'Add AWS role',
      form: {
        title: 'AWS role',
        trustRelationShip: 'Trust policy to add to your role',
        name: {
          label: 'Display name',
          placeholder: 'Role display name',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        arn: {
          label: 'ARN',
          placeholder: 'Role ARN',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        externalId: {
          label: 'External ID (optional, see trust policy)',
          placeholder: 'Role external ID',
          errors: {
            max: 'Should be at most {max} characters',
          },
        },
        create: {
          label: 'Create AWS role',
          pending: 'Creating...',
          error: 'Failed to create AWS role, try again',
          success: 'AWS role created',
        },
        update: {
          label: 'Update AWS role',
          pending: 'Updating...',
          error: 'Failed to update AWS role, try again',
          success: 'AWS role updated',
        },
        delete: {
          label: 'Delete AWS role',
          pending: 'Deleting...',
          error: 'Failed to delete AWS role, try again',
          success: 'AWS role deleted',
        },
      },
      error: 'Failed to fetch AWS roles',
    },
    projectManagement: {
      title: 'Project management',
      email: 'Email',
      role: 'Role',
      actions: 'Actions',
      promoteTooltip:
        'Admins can:{br}- Invite new users{br}- Promote members{br}- Delete the project',
      form: {
        email: {
          placeholder: 'Invited user email address',
          error: 'Should be a valid email',
        },
        submit: {
          label: 'Invite user to project',
          pending: 'Inviting user...',
          error: 'Failed to invite user, check the email and try again',
          success: 'User invited',
        },
      },
      inviteUserDialog: {
        title: 'Invite user to project',
        error: 'We encountered an error, please try again',
      },
      signUpAndInviteUserDialog: {
        subtitle:
          'This user does not have an account yet, invite them to join Anyshift?',
        button: {
          label: 'Invite user',
          pending: 'Inviting user...',
          error: 'Failed to invite user, check the email and try again',
          success: 'User invited',
        },
      },
      inviteExistingUserDialog: {
        subtitle:
          'This user already has an account, invite them to join your project?',
        instructions:
          'If the user is already in a project, ask them to leave it first',
        button: {
          label: 'Invite user',
          pending: 'Inviting user...',
          error:
            'Failed to invite user. If the user is already in a project, ask them to leave it first',
          success: 'User invited',
        },
      },
      enablePrImpact: 'Enable PR impact analysis',
      enablePrAiAnalysis: 'Enable AI insights on PR impact analysis',
      enablePrAiAnalysisDisabled: 'Enable PR impact analysis first',
      prImpactUpdated: 'PR settings updated',
    },
    regenerateGraph: {
      title: 'Anyshift Graph',
      button: {
        label: 'Refresh state and cloud in graph',
        pending: 'Refreshing state and cloud in graph...',
        error:
          'Failed to regenerate graph, check your project configuration and try again',
        success: 'State and cloud refreshed',
      },
      timeMachine: {
        button: {
          label: 'Regenerate time machine',
          pending: 'Regenerating time machine...',
          error:
            'Failed to regenerate time machine, check your project configuration and try again',
          success: 'Time machine regenerated',
        },
      },
    },
    dangerZone: {
      title: 'Danger Zone',
      deleteProject: {
        button: 'Delete project',
        dialog: {
          button: {
            label: 'Delete project',
            pending: 'Deleting project...',
            error: 'Failed to delete project, try again',
            success: 'Project deleted',
          },
          title: 'Are you sure?',
          subtitle: 'This action is irreversible',
          cancel: 'Cancel',
        },
      },
      leaveProject: {
        button: 'Leave project',
        dialog: {
          button: {
            label: 'Leave project',
            pending: 'Leaving project...',
            error: 'Failed to leave project, try again',
            success: 'Project left',
          },
          title: 'Are you sure?',
          subtitle:
            "You will lose access to this project, but it won't be deleted",
          cancel: 'Cancel',
        },
        lastAdminTooltip:
          'Promote another member to admin before leaving, or delete the project',
      },
    },
    mcpJwt: {
      title: 'Generate Claude MCP token',
      button: {
        label: 'Generate Token',
        pending: 'Generating...',
        success: 'Token generated and copied to clipboard!',
        error: 'Failed to generate JWT token.',
      },
    },
    slackApp: {
      title: 'Install Anyshift to Slack',
      button: {
        label: 'Add to Slack',
      },
    },
  },
  pullRequests: {
    title: 'Pull Requests',
    noPullRequest: 'No pull requests found',
    impacts:
      '{count, plural, one  {🔴 {count} impact} other  {🔴 {count}  impacts}}',
  },
  slackIntegration: {
    successMessage: 'Your Slack integration has been successfully linked!',
    closePrompt:
      'You can now close this page and return to your Slack workspace.',
    appOpen: 'If Slack does not open automatically, please click',
    here: ' here',
  },
  askAnnie: {
    title: 'Ask Annie',
    thinking: 'Thinking...',
    thinkingWithContent: '{content}',
    welcome: 'How can I help you?',
    placeholder: 'List all IAM users with AdministratorAccess policy',
  },
  catalog: {
    title: 'Use Cases',
    subtitle:
      'Scroll the library, pick a question that matches your use-case, or ask your own.{br}Every example below is wired to real data sources, so what you see is exactly what Annie will pull from her deep knowledge of your infrastructure.',
    tryIt: 'Try it!',
    categories: {
      onboarding: {
        title: 'Onboarding',
        questions: {
          coreServices:
            'Which core services run in production and how are they interconnected?',
          regions:
            'Which regions and availability zones host our production workloads?',
          externalTraffic:
            'Where does external traffic first enter our system?',
        },
      },
      infrastructureManagement: {
        title: 'Infrastructure Management',
        questions: {
          securityGroupImpact:
            'I am changing the security group my_prod_securitygroup attached to EC2 instance server1234. What else will be impacted?',
          terraformModule:
            'I am about to update Terraform module networking vpc. Which EC2 instances depend on it?',
        },
      },
      incidentResponse: {
        title: 'Incident Response & On-call Management',
        questions: {
          dynamoDB: 'What happened to my DynamoDB prod between April 8 and 11?',
          s3Contact: 'Who owns S3 bucket critical-data?',
        },
      },
      accessManagement: {
        title: 'Access Management',
        questions: {
          rdsInstance: "Why can't I access the production RDS instance?",
          iamUsersAdmin:
            'Which IAM users can assume roles with admin privileges and when were their credentials last rotated?',
        },
      },
      securityCompliance: {
        title: 'Security & Compliance',
        questions: {
          sshPermissions:
            'Do we have security groups permitting SSH from 0.0.0.0/0?',
          iamPolicies:
            'Which IAM policies were manually created in the AWS console?',
        },
      },
      networking: {
        title: 'Networking',
        questions: {
          vpcPeering: 'Which VPC peering connections are inactive?',
          hardcodedIPs:
            'Which services have hardcoded IPs in their configurations?',
        },
      },
      costOptimization: {
        title: 'Cost Optimization',
        questions: {
          gpuInstances:
            'Which GPU instances are running outside Terraform management?',
          EBSVolumes:
            'Which EBS volumes are unattached to any running instance?',
        },
      },
    },
  },
} as const;
